import { Code, ConnectError } from '@connectrpc/connect';
import { NextResponse } from 'next/server';

import { handleLog } from '@lib/logging/utils';

class AppError extends Error {
  constructor(
    public code: number,
    message: string,
  ) {
    super(message);
    this.name = this.constructor.name;
  }
}

export class InternalError extends AppError {
  constructor(message: string) {
    super(Code.Internal, message);
  }
}

export class PermissionError extends AppError {
  constructor(message: string) {
    super(Code.PermissionDenied, message);
  }
}

export class ValidationError extends AppError {
  constructor(message: string) {
    super(Code.InvalidArgument, message);
  }
}

export class NotFoundError extends AppError {
  constructor(message: string) {
    super(Code.NotFound, message);
  }
}

export class UnauthenticatedError extends AppError {
  constructor(message: string) {
    super(Code.Unauthenticated, message);
  }
}

// Centralized error handling for API errors
export function handleAPIError(errorData: {
  error: string;
  code: number;
}): never {
  switch (errorData.code) {
    case Code.NotFound:
      throw new NotFoundError(errorData.error);
    case Code.InvalidArgument:
      throw new ValidationError(errorData.error);
    case Code.Unauthenticated:
      throw new UnauthenticatedError(errorData.error);
    case Code.PermissionDenied:
      throw new PermissionError(errorData.error);
    default:
      throw new InternalError(errorData.error);
  }
}

// Centralized error handling for fetch or network errors
export function handleFetchError(error: unknown): never {
  if (error instanceof AppError) {
    throw error;
  }

  throw new InternalError(
    error instanceof Error ? error.message : 'An unknown error occurred',
  );
}

/**
 * Handles errors and returns a standardized JSON response.
 * If the error is a ConnectError, it includes the error message and code.
 * Otherwise, it returns a generic error message.
 *
 * @param error - The error object to handle.
 * @param message - A fallback error message if the error is not a ConnectError.
 * @returns A Next.js JSON response with the appropriate error message and status code.
 */
export async function errorResponse(error: unknown, message: string) {
  // Ensure the error is an instance of Error
  if (!(error instanceof Error)) {
    error = new Error(String(error));
  }

  handleLog('error', {
    message: 'Error in response:',
    metadata: {
      message,
      error: error instanceof Error ? error.message : 'Unknown error',
    },
  });

  // Convert the error to a ConnectError
  const connectErr = ConnectError.from(error);

  // Determine the appropriate status code
  let statusCode;

  if (connectErr.code === Code.NotFound) {
    statusCode = 404;
  } else if (connectErr.code === Code.InvalidArgument) {
    statusCode = 400;
  } else if (connectErr.code === Code.Unauthenticated) {
    statusCode = 401;
  } else if (connectErr.code === Code.PermissionDenied) {
    statusCode = 403;
  } else {
    statusCode = 500;
  }

  // Return the error response
  return NextResponse.json(
    {
      error: connectErr.message || message,
      code: connectErr.code,
    },
    { status: statusCode },
  );
}
